// frontend/src/api/oauthApi.js

import { getApiUrl, getFetchOptions } from '../utils/apiConfig.js';

const apiRequest = async (endpoint, options = {}) => {
  const url = getApiUrl(endpoint);
  const fetchOptions = getFetchOptions(options);
  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * OAuth API client for enterprise authentication
 */

/**
 * Get available OAuth providers
 */
export const getOAuthProviders = async () => {
  try {
    const response = await apiRequest('/api/auth/oauth/providers', {
      method: 'GET',
    });

    if (response.success) {
      return response.providers;
    }
    throw new Error(response.error || 'Failed to get OAuth providers');
  } catch (error) {
    console.error('Error getting OAuth providers:', error);
    throw error;
  }
};

/**
 * Start OAuth authorization flow
 */
export const startOAuthFlow = async (provider, redirectUri, options = {}) => {
  try {
    const response = await apiRequest(`/api/auth/oauth/authorize/${provider}`, {
      method: 'POST',
      body: JSON.stringify({
        redirectUri,
        ...options,
      }),
    });

    if (response.success) {
      return {
        authorizationUrl: response.authorizationUrl,
        state: response.state,
        provider: response.provider,
      };
    }
    throw new Error(response.error || 'Failed to start OAuth flow');
  } catch (error) {
    console.error('Error starting OAuth flow:', error);
    throw error;
  }
};

/**
 * Handle OAuth callback
 */
export const handleOAuthCallback = async (provider, code, state) => {
  try {
    const response = await apiRequest(`/api/auth/oauth/callback/${provider}`, {
      method: 'POST',
      body: JSON.stringify({
        code,
        state,
      }),
    });

    if (response.success) {
      return {
        token: response.token,
        user: response.user,
        authMethod: response.authMethod,
        provider: response.provider,
        sessionId: response.sessionId,
      };
    }
    throw new Error(response.error || 'OAuth authentication failed');
  } catch (error) {
    console.error('Error handling OAuth callback:', error);
    throw error;
  }
};

/**
 * Refresh OAuth token
 */
export const refreshOAuthToken = async (provider, refreshToken) => {
  try {
    const response = await apiRequest(`/api/auth/oauth/refresh/${provider}`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (response.success) {
      return {
        accessToken: response.accessToken,
        expiresIn: response.expiresIn,
        tokenType: response.tokenType,
      };
    }
    throw new Error(response.error || 'Failed to refresh OAuth token');
  } catch (error) {
    console.error('Error refreshing OAuth token:', error);
    throw error;
  }
};

/**
 * Revoke OAuth token
 */
export const revokeOAuthToken = async (provider, token, tokenType = 'access_token') => {
  try {
    const response = await apiRequest(`/api/auth/oauth/revoke/${provider}`, {
      method: 'POST',
      body: JSON.stringify({
        token,
        tokenType,
      }),
    });

    if (response.success) {
      return true;
    }
    throw new Error(response.error || 'Failed to revoke OAuth token');
  } catch (error) {
    console.error('Error revoking OAuth token:', error);
    throw error;
  }
};

/**
 * Get OAuth service status
 */
export const getOAuthStatus = async () => {
  try {
    const response = await apiRequest('/api/auth/oauth/status', {
      method: 'GET',
    });

    if (response.success) {
      return {
        status: response.status,
        validation: response.validation,
        timestamp: response.timestamp,
      };
    }
    throw new Error(response.error || 'Failed to get OAuth status');
  } catch (error) {
    console.error('Error getting OAuth status:', error);
    throw error;
  }
};

/**
 * Test OAuth provider connection
 */
export const testOAuthProvider = async provider => {
  try {
    const response = await apiRequest(`/api/auth/oauth/test/${provider}`, {
      method: 'POST',
    });

    if (response.success) {
      return {
        provider: response.provider,
        configValid: response.configValid,
        capabilities: response.capabilities,
        discovery: response.discovery,
      };
    }
    throw new Error(response.error || 'OAuth provider test failed');
  } catch (error) {
    console.error('Error testing OAuth provider:', error);
    throw error;
  }
};

export default {
  getOAuthProviders,
  startOAuthFlow,
  handleOAuthCallback,
  refreshOAuthToken,
  revokeOAuthToken,
  getOAuthStatus,
  testOAuthProvider,
};
